import { initializeApp } from "firebase/app";
import { ProductItemTypes, UserDataTypes, UserProductTypes } from "../models/types"

import {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signOut,
    Auth,
    EmailAuthProvider,
    reauthenticateWithCredential
} from "firebase/auth";
import {
    getFirestore,
    collection,
    addDoc,
    doc,
    getDoc,
    setDoc, query, where, getDocs, Firestore, Timestamp, updateDoc, deleteDoc,
    arrayUnion
} from "firebase/firestore";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
const auth: Auth = getAuth(app);
const db: Firestore = getFirestore(app);

//const [updateUserState] = UserLoginStore((state) => [state.UpdateUserLoginState]);

// sign-in
const logInWithEmailAndPassword = async (email: string, password: string) => {
    try {
        await signInWithEmailAndPassword(auth, email, password);
        await getUserData(); // Fetch the user data
        migrateOldAccounts(); // Fix older accounts that use 'UID' instead of 'ID'
    } catch (err: any) {
        throw err.code
    }
};

// save user data
const saveUserData = async (userData: UserDataTypes) => {
    const user = auth.currentUser;

    try {
        if (user) {
            const docRef = doc(db, "users", user.uid); // Using user.uid as the Firestore document ID
            await setDoc(docRef, userData, { merge: true }); // Merge option keeps existing fields and updates new ones
            console.log("User data saved successfully!");
        } else {
            console.log("No user is signed in.");
        }
    } catch (error) {
        console.error("Error saving user data:", error);
    }
};

// sign-up
const registerWithEmailAndPassword = async (name: string, company: string, email: string, password: string) => {
    try {
        const res = await createUserWithEmailAndPassword(auth, email, password);
        const user = res.user;
        /*
        const actionCodeSettings = {
            url: 'https://www.artomeinstaller.com/?email={$user@example.com}',
            iOS: {
                bundleId: 'com.artome-installer.ios'
            },
            android: {
                packageName: 'com.artome-installer.android',
                installApp: true,
                minimumVersion: '12'
            }
        }
        await sendEmailVerification(user, actionCodeSettings);
        */
        await saveUserData(
            {
                id: user.uid,
                name,
                company,
                email,
                register_method: "artomeinstaller.com"
            }
        );
    } catch (err: any) {
        throw err.code
    }
};

// send user password reset
const sendPasswordReset = async (email: string): Promise<void> => {
    try {
        await sendPasswordResetEmail(auth, email);
        console.log("Check your email for the password reset link!");
    } catch (error) {
        const errorCode = (error as { code: string }).code;
        let errorMessage: string;

        switch (errorCode) {
            case 'auth/user-not-found':
                errorMessage = 'No user found with this email address.';
                break;
            case 'auth/invalid-email':
                errorMessage = 'The email address is not valid.';
                break;
            case 'auth/too-many-requests':
                errorMessage = 'Too many requests. Please try again later.';
                break;
            default:
                errorMessage = 'An error occurred. Please try again.';
                break;
        }

        console.error("Error sending password reset email:", errorMessage);
    }
};

// log-out user
const logout = async () => {
    await signOut(auth);
};

// get user data
const getUserData = async () => {
    const user = auth.currentUser;
    if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        console.log("User data fetched successfully!");

        if (docSnap.exists()) {
            const userData = docSnap.data() as UserDataTypes;

            // Check if the user is marked for deletion
            if (userData.markedForDeletion) {
                console.warn("User account is marked for deletion.");
                await logout();
                return null;
            }

            return userData;
        } else {
            console.log("No user data found!");
            return null;
        }
    } else {
        console.log("No user is signed in.");
        return null;
    }
};

// get user products
const getUserProductsData = async () => {
    const user = auth.currentUser;

    if (user) {
        const q = query(collection(db, "web_installer"), where("uid", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const products: UserProductTypes[] = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            const product: UserProductTypes = {
                ...data,
                setup_date: data.setup_date
                    ? (data.setup_date as Timestamp).toDate().toISOString().split("T")[0]
                    : ""
            } as UserProductTypes;
            products.push(product);
        });
        return products;
    } else {
        throw new Error("No user is currently signed in.");
    }
};

// get user product item
const getUserProductItemData = async (artomeSerialNo: string) => {
    const user = auth.currentUser;

    if (user) {
        const q = query(
            collection(db, "web_installer"),
            where("uid", "==", user.uid),
            where("artome_serial_no", "==", artomeSerialNo.trim().toUpperCase())
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const data = doc.data();
            // Guarded and locale-independent, matching getUserProductsData. The
            // unguarded `.toDate()` here threw for any document without a
            // setup_date, and the localized string sorted and parsed wrongly.
            const product: ProductItemTypes = {
                ...data,
                setup_date: data.setup_date
                    ? (data.setup_date as Timestamp).toDate().toISOString().split("T")[0]
                    : "",
                re_setup_date: data.re_setup_date
                    ? (data.re_setup_date as Timestamp).toDate().toISOString().split("T")[0]
                    : "",
            } as ProductItemTypes;
            return product;
        } else {
            throw new Error("No product found for the current user with the given artome_serial_no.");
        }
    } else {
        throw new Error("No user is currently signed in.");
    }
};

// create & update products registration records
export interface RegistrationResult {
    id: string;
    mode: "created" | "updated";
    /** Owner recorded before this registration, when ownership changed hands. */
    previousOwner?: string;
}

/**
 * Create or update a product registration.
 *
 * Reads `auth.currentUser` directly rather than wrapping the write in
 * `onAuthStateChanged`. The previous form resolved as soon as the listener was
 * registered — before the write ran — so callers could neither await the result
 * nor catch failures, and every call left an un-unsubscribed listener that
 * re-ran the whole upsert on any later auth state change. Callers sit behind
 * `ProtectedRoute`, so the user is already resolved.
 */
const registrationRecords = async (
    epsonSerNo: string,
    artomeSerNo: string,
    product: string,
    owner: string
): Promise<RegistrationResult> => {
    const user = auth.currentUser;
    if (!user) throw new Error("NOT_AUTHENTICATED");

    // Normalize once, then use the SAME value to query and to write. Previously
    // the query used the raw input while the create branch wrote uppercase, so a
    // lowercase serial never matched the stored document and silently created a
    // duplicate registration instead of transferring ownership.
    const artome = artomeSerNo.trim().toUpperCase();
    const epson = epsonSerNo.trim().toUpperCase();
    const productName = product.trim().toUpperCase();

    // Deliberately NOT filtered by `uid`: a device may already be registered by
    // a different installer, and finding that record is the whole point — it is
    // what moves the prior owner into `previous_owners` instead of creating a
    // second document for the same serial.
    //
    // This makes the query depend on a security rule that does not reference
    // `resource.data.uid`, because Firestore validates a query against the rule
    // rather than against the documents it returns: any `web_installer` read rule
    // requiring `request.auth.uid == resource.data.uid` rejects this query
    // outright with `permission-denied`, for every serial, registered or not.
    // See firebase/firestore.rules in artome_connect — the `list` allowance
    // there exists for this query.
    const q = query(
        collection(db, "web_installer"),
        where("artome_serial_no", "==", artome)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
        const docToUpdate = querySnapshot.docs[0];
        const docData = docToUpdate.data();
        // Defensive: documents written before `previous_owners` existed have no
        // such field, and nothing guarantees the field's type on historical
        // records. A non-array here used to throw "push is not a function".
        const previousOwners = Array.isArray(docData.previous_owners)
            ? docData.previous_owners
            : [];
        const priorOwner: string | undefined = docData.product_owner;

        if (priorOwner) {
            // Omit `date` entirely when the record carries neither timestamp.
            // Firestore rejects a write containing `undefined` outright
            // (`invalid-argument`), and documents predating `setup_date` do
            // exist — so building `{ date: undefined }` failed the whole
            // registration for exactly those devices.
            const priorDate = docData.re_setup_date ?? docData.setup_date;
            previousOwners.push(
                priorDate ? { owner: priorOwner, date: priorDate } : { owner: priorOwner }
            );
        }

        await updateDoc(doc(db, "web_installer", docToUpdate.id), {
            artome_product: productName,
            product_owner: owner,
            language: navigator.language,
            epson_serial_no: epson,
            previous_owners: previousOwners,
            re_setup_date: new Date(),
            uid: user.uid,
            role: docData.role || "user",
        });

        return {
            id: docToUpdate.id,
            mode: "updated",
            previousOwner:
                priorOwner && priorOwner !== owner ? priorOwner : undefined,
        };
    }

    const created = await addDoc(collection(db, "web_installer"), {
        artome_product: productName,
        product_owner: owner,
        artome_serial_no: artome,
        language: navigator.language,
        epson_serial_no: epson,
        previous_owners: [],
        setup_date: new Date(),
        uid: user.uid,
        role: "user",
    });

    return { id: created.id, mode: "created" };
};


// Remove the product
/**
 * Delete a registration by Artome serial.
 *
 * Same unwrap as `registrationRecords`: the caller deletes in a loop and needs
 * each delete to actually settle. The old listener-wrapped form returned before
 * deleting, so the grid removed rows locally while the writes might never land.
 *
 * Scoped to the caller's own `uid`. The previous form matched on serial alone,
 * which had two consequences: the security rules reject a `web_installer` query
 * that is not constrained by `uid` (so every delete failed with
 * `permission-denied` for anyone without an admin role), and where a serial had
 * somehow been registered twice, `docs[0]` could be *another* installer's record.
 * Both callers delete from the signed-in user's own device list, so the filter
 * costs nothing and is the correct scope.
 */
const removeProduct = async (artomeSerNo: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error("NOT_AUTHENTICATED");

    const artome = artomeSerNo.trim().toUpperCase();
    const q = query(
        collection(db, "web_installer"),
        where("uid", "==", user.uid),
        where("artome_serial_no", "==", artome)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return;

    await deleteDoc(doc(db, "web_installer", querySnapshot.docs[0].id));
};

// Update user data
const updateUserData = async (field: string, value: string) => {
    const user = auth.currentUser;
    if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const userData = docSnap.data();
            const oldValue = userData[field] || null;

            const updates: { [key: string]: any } = {
                [field]: value,
            };

            if (oldValue) {
                const oldFieldArray = `${field}_history`;
                updates[oldFieldArray] = arrayUnion(oldValue);
            }

            await updateDoc(docRef, updates);
            console.log("User data saved successfully!");
        } else {
            console.log("No such document!");
        }
    } else {
        console.log("No user is signed in.");
    }
};

// Mark user for deletion
const deleteUserAccount = async () => {
    const user = auth.currentUser;
    if (user) {
        try {
            // Step 1: Delete user data from Firestore
            const docRef = doc(db, "users", user.uid);
            await deleteDoc(docRef);
            console.log("User data deleted from Firestore successfully!");

            // Step 2: Delete user from Firebase Authentication
            await user.delete();
            console.log("User account deleted from Firebase Authentication successfully!");
        } catch (err: any) {
            if (err.code === "auth/requires-recent-login") {
                // If the user needs to re-authenticate before deletion
                console.error("Error: User needs to re-authenticate before deletion.");
                // Optionally, you can call reauthenticateUser to prompt for re-authentication
            } else {
                console.error("Error deleting user:", err);
            }
        }
    } else {
        console.log("No user is signed in.");
    }
};

const reauthenticateUser = async (email: string, password: string) => {
    const user = auth.currentUser;
    if (user) {
        const credential = EmailAuthProvider.credential(email, password);
        try {
            await reauthenticateWithCredential(user, credential);
            console.log("User re-authenticated successfully.");
            await deleteUserAccount(); // After re-authentication, delete the account
        } catch (err) {
            console.error("Error during re-authentication:", err);
        }
    }
};

/**
 * Backfill `id` from `uid` on the signing-in user's own profile.
 *
 * Only ever touches `users/{currentUser.uid}`. The previous form queried the
 * whole `users` collection for documents needing the backfill, which no client
 * is allowed to do — the rules restrict reads to your own document — so it threw
 * `permission-denied` on every sign-in by a non-admin and migrated nothing. It
 * also awaited inside a `forEach` callback, so the writes were unobserved
 * promises the caller could neither await nor catch.
 *
 * Each user's own document is the only one they can fix, and they pass through
 * here on every sign-in, so per-user backfill covers the same ground.
 */
const migrateOldAccounts = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) return;

        const userData = docSnap.data();
        if (userData.uid && !userData.id) {
            await updateDoc(docRef, { id: userData.uid });
            console.log(`User ${user.uid} updated: uid copied to id`);
        }
    } catch (error) {
        console.error("Error migrating old account:", error);
    }
};

export {
    auth,
    logInWithEmailAndPassword,
    registerWithEmailAndPassword,
    sendPasswordReset,
    logout,
    getUserData,
    getUserProductsData,
    getUserProductItemData,
    removeProduct,
    updateUserData,
    registrationRecords,
    reauthenticateUser,
    migrateOldAccounts,
};